import { IconDownload } from '@contrast-security-inc/design-system-foundations'
import React from 'react'
import { SubmitFormItem } from '../../../../util'
import { ExportFileType, exportLibraryData } from './helpers'
import {
  Menu,
  MenuItem,
  MenuTrigger,
  PopoverMenu
} from '@contrast-security-inc/design-system-popovers'
import { ActionButton } from '@contrast-security-inc/design-system-actions'
import { useIntl } from 'react-intl'

export const actionMenuTestID = 'library-summary-export-action-menu'
export const actionTestID = 'library-summary-export-action-icon'

interface Props {
  organizationID: string
  applicationID?: string
  serverID?: string
  excludedHashes: string[]
  selectedLibraryHashes?: string[]
  filters?: {
    filterText: string
    quickFilter: string
    aggregations: Record<string, string[]>
  }
  additionalFilters?: { [key: string]: any }
  popoverMenuKey?: string
}

type UpdateAggregationsFn = (props: {
  applicationID?: string
  aggregations: Record<string, string[]>
}) => Record<string, string[]>

export const mergeApplicationIds: UpdateAggregationsFn = ({
  applicationID,
  aggregations
}) => {
  if (typeof applicationID === 'undefined') {
    return aggregations
  }
  return {
    ...aggregations,
    applications: Array.isArray(aggregations.applications)
      ? [...aggregations.applications, applicationID]
      : [applicationID]
  }
}

export interface BuildFormDataProps {
  applicationID?: string
  excludedHashes: string[]
  selectedLibraryHashes?: string[]
  serverID?: string
  baseURL: string
  filters:
    | {
        filterText: string
        quickFilter: string
        aggregations: Record<string, string[]>
      }
    | undefined
}

type BuildFormDataFn = (props: BuildFormDataProps) => SubmitFormItem[]

export const buildFormData: BuildFormDataFn = (props: BuildFormDataProps) => {
  const {
    selectedLibraryHashes,
    applicationID,
    filters,
    excludedHashes,
    baseURL,
    serverID
  } = props

  const requestData: SubmitFormItem[] = []

  if (filters) {
    const { aggregations, filterText, quickFilter } = filters

    const mappedAggregations = mergeApplicationIds({
      applicationID,
      aggregations
    })

    requestData.push(
      ...[
        { name: 'filterText', value: filterText },
        { name: 'quickFilter', value: quickFilter },

        ...filtersToInputs(mappedAggregations)
      ]
    )
  }

  if (serverID) {
    requestData.push({ name: 'servers', value: serverID })
  }

  if (baseURL.includes('bulk/export')) {
    requestData.push(
      ...excludedHashes.map((value) => ({
        name: 'excludedHashes',
        value
      }))
    )

    return requestData
  } else {
    if (selectedLibraryHashes && selectedLibraryHashes.length > 0) {
      requestData.push(
        ...selectedLibraryHashes.map((value) => ({
          name: 'hashes',
          value
        }))
      )
    }
  }

  return requestData
}

export function ExportLibrariesAction({
  filters,
  organizationID,
  applicationID,
  excludedHashes,
  selectedLibraryHashes = [],
  serverID,
  popoverMenuKey
}: Props) {
  const { formatMessage } = useIntl()
  const baseURL =
    selectedLibraryHashes.length > 0
      ? `${organizationID}/libraries/export`
      : `${organizationID}/libraries/bulk/export`

  const exportAs = (fileType: ExportFileType) => () =>
    exportLibraryData(
      fileType,
      buildFormData({
        filters,
        applicationID,
        excludedHashes,
        selectedLibraryHashes,
        baseURL,
        serverID
      }),
      baseURL,
      applicationID
    )

  return (
    <PopoverMenu placement="left" id={popoverMenuKey}>
      <Menu>
        <MenuItem
          testId="library-summary-export-csv-option-icon"
          onClick={exportAs('CSV')}
        >
          CSV
        </MenuItem>
        <MenuItem
          testId="library-summary-export-xml-option-icon"
          onClick={exportAs('XML')}
        >
          XML
        </MenuItem>
        <MenuItem
          testId="library-summary-export-xlsx-option-icon"
          onClick={exportAs('XLSX')}
        >
          XLSX
        </MenuItem>
      </Menu>
      <MenuTrigger testId={actionMenuTestID}>
        {({ isOpen }) => (
          <ActionButton
            testId={actionTestID}
            IconButtonProps={{
              Icon: IconDownload,
              'aria-label': formatMessage({ id: 'action_export_data' })
            }}
            TooltipProps={{
              placement: 'top',
              body: formatMessage({ id: 'action_export_data' }),
              enabled: !isOpen
            }}
          />
        )}
      </MenuTrigger>
    </PopoverMenu>
  )
}

const mapUsageAggregationsToFormItem = (
  acc: SubmitFormItem[],
  value: string[]
) => [
  ...acc,
  {
    name: 'includeUsed',
    value: value.indexOf('used') > -1 ? 'true' : 'false'
  },
  {
    name: 'includeUnused',
    value: value.indexOf('unused') > -1 ? 'true' : 'false'
  }
]

function filtersToInputs(filters: Record<string, string[]>): SubmitFormItem[] {
  return Object.keys(filters).reduce((acc, key) => {
    const filterValue = filters[key]
    return key === 'usage'
      ? mapUsageAggregationsToFormItem(acc, filterValue)
      : [
          ...acc,
          ...filters[key].map((value) => ({
            name: filterKeyToInputName(key),
            value
          }))
        ]
  }, [] as SubmitFormItem[])
}

function filterKeyToInputName(name: string): string {
  return name === 'applications' ? 'apps' : name
}
